import { mapValues } from 'lodash'
import { BLOCK_TYPE, LINK_IN_BIO_TYPE_ENUM } from 'state/tasks'
import { ScheduleType } from 'state/registrations'
import * as yup from 'yup'
import i18n from 'config/i18n'
import { getTimeZonedDateNow, parseJsDate } from 'services/dates'
import { ERROR_MESSAGES } from './errorMessages'
import { ERROR_CODES } from 'state/error'
import { validateStringOrNumberArrayField } from 'components/utils/errors'
import { CampaignState, CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import _ from 'lodash'
import { validateDateLaterThenNow } from 'components/utils/index'

export const TASK_TITLE_SCHEMA = (isCampaignDraftOrForEdit: boolean) => {
    return yup
        .string()
        .nullable()
        .test({
            message: ERROR_MESSAGES['title'][ERROR_CODES.INPUT_REQUIRED],
            test: value => {
                if (isCampaignDraftOrForEdit) {
                    return true
                } else {
                    return Boolean(value)
                }
            },
        })
        .max(150, i18n.t('errorMessage.validation.enterTaskTitleCharacterLimitOneHundredFifty'))
}

export const STEP_DATA_SCHEMAS = (isCampaignDraftOrForEdit: boolean) => {
    return {
        title: yup
            .string()
            .nullable()
            .test({
                message: ERROR_MESSAGES['steps']['title'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: string | null) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
        event_name: yup
            .string()
            .nullable()
            .test({
                message: ERROR_MESSAGES['steps']['event_name'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: string | null) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
        description: yup
            .string()
            .nullable()
            .max(200, i18n.t('errorMessage.validation.enterProductDescriptionCharacterLimit')),
        event_information: yup
            .array()
            .of(
                yup.string().test({
                    message: ERROR_MESSAGES['steps']['event_information'][ERROR_CODES.INPUT_REQUIRED],
                    test: (value: string | null) => {
                        if (isCampaignDraftOrForEdit) {
                            return true
                        } else {
                            return Boolean(value)
                        }
                    },
                }),
            )
            .nullable(),
        things_to_note: yup
            .array()
            .of(
                yup
                    .string()
                    .nullable()
                    .test({
                        message: ERROR_MESSAGES['steps']['things_to_note'][ERROR_CODES.INPUT_REQUIRED],
                        test: (value: string | null) => {
                            if (isCampaignDraftOrForEdit) {
                                return true
                            } else {
                                return Boolean(value)
                            }
                        },
                    }),
            )
            .nullable(),
        product_name: yup
            .string()
            .nullable()
            .test({
                message: ERROR_MESSAGES['steps']['product_name'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: string | null) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
        example_photos: yup
            .array()
            .of(
                yup.object({
                    image: yup.string(),
                    img_main: yup.string(),
                    uuid: yup.string(),
                }),
            )
            .nullable(),
        example_photos_description: yup.string().nullable(),
        how_to_buy: yup
            .array()
            .of(
                yup
                    .string()
                    .nullable()
                    .test({
                        message: ERROR_MESSAGES['steps']['how_to_buy'][ERROR_CODES.INPUT_REQUIRED],
                        test: (value: string | null) => {
                            if (isCampaignDraftOrForEdit) {
                                return true
                            } else {
                                return Boolean(value)
                            }
                        },
                    }),
            )
            .nullable(),
        submission_requirement_description: yup
            .string()
            .nullable()
            .test({
                message: ERROR_MESSAGES['steps']['submission_requirement_description'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: string | null) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),

        caption_requirement_dos: yup
            .array()
            .of(yup.string())
            .nullable()
            .test({
                message: ERROR_MESSAGES['steps']['caption_requirement_dos'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: Record<string, string | number>[]) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return validateStringOrNumberArrayField(value)
                    }
                },
            }),
        caption_requirement_donts: yup
            .array()
            .of(yup.string())
            .nullable()
            .test({
                message: ERROR_MESSAGES['steps']['caption_requirement_donts'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: Record<string, string | number>[]) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return validateStringOrNumberArrayField(value)
                    }
                },
            }),
        caption_requirement_hashtags: yup
            .array()
            .of(
                yup
                    .string()
                    .nullable()
                    .matches(/^(?!#)/, i18n.t('errorMessage.validation.enterHashtag'))
                    .test({
                        message: ERROR_MESSAGES['steps']['caption_requirement_hashtags'][ERROR_CODES.INPUT_REQUIRED],
                        test: (value: string | null) => {
                            if (isCampaignDraftOrForEdit) {
                                return true
                            } else {
                                return Boolean(value)
                            }
                        },
                    }),
            )
            .nullable(),
        media_requirement_dos: yup
            .array()
            .of(yup.string().nullable())
            .nullable()
            .test({
                message: ERROR_MESSAGES['steps']['media_requirement_dos'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: Record<string, string | number>[]) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return validateStringOrNumberArrayField(value)
                    }
                },
            }),

        media_requirement_donts: yup
            .array()
            .of(yup.string().nullable())
            .nullable()
            .test({
                message: ERROR_MESSAGES['steps']['media_requirement_donts'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: Record<string, string | number>[]) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return validateStringOrNumberArrayField(value)
                    }
                },
            }),

        media_requirement_tags: yup
            .array()
            .of(
                yup
                    .string()
                    .nullable()
                    .matches(/^(?!@)/, i18n.t('errorMessage.validation.enterProfileTag'))
                    .test({
                        message: ERROR_MESSAGES['steps']['media_requirement_tags'][ERROR_CODES.INPUT_REQUIRED],
                        test: (value: string | null) => {
                            if (isCampaignDraftOrForEdit) {
                                return true
                            } else {
                                return Boolean(value)
                            }
                        },
                    }),
            )
            .nullable(),
        requirement_text: yup
            .string()
            .nullable()
            .test({
                message: ERROR_MESSAGES['steps']['requirement_text'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: string | null) => {
                    if (isCampaignDraftOrForEdit) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
        link_in_bio_type: yup
            .mixed<null | LINK_IN_BIO_TYPE_ENUM>()
            .oneOf([
                null,
                LINK_IN_BIO_TYPE_ENUM.NO_LINK,
                LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK,
                LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK,
            ])
            .nullable(),
        link_in_bio_url: yup
            .string()
            .nullable()
            .when('link_in_bio_type', {
                is: LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK,
                then: yup
                    .string()
                    .nullable()
                    .url(i18n.t('errorMessage.validation.enterUrlLink'))
                    .test({
                        message: ERROR_MESSAGES['steps']['link_in_bio_url'][ERROR_CODES.INPUT_REQUIRED],
                        test: (value: string | null) => {
                            if (isCampaignDraftOrForEdit) {
                                return true
                            } else {
                                return Boolean(value)
                            }
                        },
                    }),
            }),
        link_in_bio_duration: yup.number().when('link_in_bio_type', (link_in_bio_type: LINK_IN_BIO_TYPE_ENUM) => {
            if (
                link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.PREDEFINED_LINK ||
                link_in_bio_type == LINK_IN_BIO_TYPE_ENUM.DYNAMIC_LINK
            ) {
                return yup
                    .number()
                    .nullable()
                    .integer(i18n.t('errorMessage.validation.enterNumberDays'))
                    .min(0, i18n.t('errorMessage.validation.enterNonNegativeNumbers'))
                    .test({
                        message: ERROR_MESSAGES['steps']['link_in_bio_duration'][ERROR_CODES.INPUT_REQUIRED],
                        test: (value: number | null) => {
                            if (isCampaignDraftOrForEdit || value === 0) {
                                return true
                            } else {
                                return Boolean(value)
                            }
                        },
                    })
            } else {
                return yup.number().nullable()
            }
        }),
        extra_data: yup
            .array()
            .of(
                yup.object().shape({
                    title: yup.string().nullable(),
                    description: yup.string().nullable(),
                }),
            )
            .nullable()
            .test({
                message: ERROR_MESSAGES['steps']['extra_data'][ERROR_CODES.INPUT_REQUIRED],
                test: (values: { title: string; description: string }[] | null) => {
                    let isAllFilled = true
                    if (!isCampaignDraftOrForEdit && Array.isArray(values) && values.length > 0) {
                        values.forEach((value: { title: string; description: string }) => {
                            if (!Boolean(value['title']) || !Boolean(value['description'])) {
                                isAllFilled = false
                            }
                        })
                    }
                    return isAllFilled
                },
            }),
    }
}

interface CompareStartAndEndDateState extends ScheduleType {
    isRegistrationTimeComparison: boolean
}

export const STEP_TIME_WINDOW_SCHEMA = (
    campaign: CampaignState,
    initialValues: any,
    isExternalUser: boolean,
    previousStepTypeOrIndex?: string | null,
    compareStartAndEndDate: CompareStartAndEndDateState = {
        start: null,
        end: null,
        isRegistrationTimeComparison: false,
    },
    isPublish?: { current: boolean },
) => {
    const isCampaignDraftOrForEdit =
        campaign &&
        (campaign.status === CAMPAIGN_STATUS_ENUM.DRAFT || campaign.status === CAMPAIGN_STATUS_ENUM.FOR_EDIT)

    const isCampaignLive = campaign && campaign.status === CAMPAIGN_STATUS_ENUM.LIVE
    return yup
        .object({
            start: yup
                .string()
                .nullable()
                .test({
                    message: ERROR_MESSAGES['time_window']['start'][ERROR_CODES.INPUT_REQUIRED],
                    test: value => {
                        if (isCampaignDraftOrForEdit) {
                            return true
                        } else {
                            return Boolean(value)
                        }
                    },
                })
                .test({
                    message: ERROR_MESSAGES['time_window']['end'][ERROR_CODES.END_TIME_MUST_BE_LATER_THAN_START_TIME],
                    test: function (value) {
                        const endDateString: string | null = this.parent?.end
                        if (!endDateString || !value) {
                            return true
                        }
                        const endDate = new Date(parseJsDate(endDateString, false))
                        const startDate = new Date(parseJsDate(value, false))
                        return endDate >= startDate
                    },
                })
                .test({
                    message: ERROR_MESSAGES['time_window']['start'][ERROR_CODES.SCHEDULE_MUST_LATER_THAN_NOW],
                    test: function (value) {
                        if (!value) {
                            return true
                        }
                        const initialStartDateString = initialValues
                            ? _.get(initialValues, this.path, undefined)
                            : undefined
                        const initialStartDate = initialStartDateString ? new Date(initialStartDateString) : undefined
                        const startDate = new Date(value)
                        if (initialStartDate && initialStartDate.getTime() === startDate.getTime()) {
                            return true
                        } else {
                            return validateDateLaterThenNow(startDate, campaign.country)
                        }
                    },
                })
                .test({
                    message: ERROR_MESSAGES['time_window']['start'][ERROR_CODES.PAST_SCHEDULE_UPDATE_NOT_ALLOWED],
                    test: function (value) {
                        if (!isCampaignLive || !value) {
                            return true
                        }
                        const now = getTimeZonedDateNow(campaign.country)
                        const initialStartDateString = initialValues
                            ? _.get(initialValues, this.path, undefined)
                            : undefined

                        // Cast string to date
                        const initialStartDate = initialStartDateString ? new Date(initialStartDateString) : undefined
                        const startDate = new Date(parseJsDate(value, false))

                        if (initialStartDate && initialStartDate.getTime() < now.getTime()) {
                            return initialStartDate ? initialStartDate.getTime() === startDate.getTime() : true
                        }

                        return true
                    },
                })
                .test({
                    message: ERROR_MESSAGES['time_window']['start'][ERROR_CODES.SCHEDULE_MUST_LATER_THAN_PARENT_STEP],
                    test: function (value) {
                        if (!previousStepTypeOrIndex || !value) {
                            return true
                        } else {
                            const currentStepStartDate = new Date(parseJsDate(value, false))
                            const rootSchemaOptions: any = this.options
                            const { from = null } = rootSchemaOptions
                            const rootValue = from && from[2] ? from[2].value : null
                            const previousStepTime =
                                rootValue && rootValue.type === 'custom'
                                    ? rootValue.steps[previousStepTypeOrIndex].time_window
                                    : rootValue
                                      ? rootValue[previousStepTypeOrIndex].time_window
                                      : null
                            const previousStepStartTime = previousStepTime ? previousStepTime?.start : null
                            const compareStartTime = previousStepTime
                                ? new Date(parseJsDate(previousStepStartTime, false))
                                : null
                            if (compareStartTime) {
                                return currentStepStartDate >= compareStartTime
                            } else {
                                return true
                            }
                        }
                    },
                })
                .test({
                    message: compareStartAndEndDate.isRegistrationTimeComparison
                        ? ERROR_MESSAGES['time_window']['start'][
                              ERROR_CODES.REGISTRATION_SCHEDULE_MUST_START_EARLIER_THAN_TASK
                          ]
                        : ERROR_MESSAGES['time_window']['start'][
                              ERROR_CODES.SCHEDULE_MUST_START_LATER_THAN_REGISTRATION
                          ],
                    test: function (value) {
                        if (!compareStartAndEndDate.start || !value) {
                            return true
                        } else {
                            const currentStepStartDate = new Date(parseJsDate(value, false))
                            const compareStartTime = new Date(compareStartAndEndDate.start)
                            if (compareStartTime) {
                                if (compareStartAndEndDate.isRegistrationTimeComparison) {
                                    return currentStepStartDate <= compareStartTime
                                } else {
                                    return currentStepStartDate >= compareStartTime
                                }
                            } else {
                                return true
                            }
                        }
                    },
                }),
            end: yup
                .string()
                .nullable()
                .test({
                    message: ERROR_MESSAGES['time_window']['end'][ERROR_CODES.INPUT_REQUIRED],
                    test: value => {
                        if (isCampaignDraftOrForEdit) {
                            return true
                        } else {
                            return Boolean(value)
                        }
                    },
                })
                .test({
                    message: ERROR_MESSAGES['time_window']['end'][ERROR_CODES.END_TIME_MUST_BE_LATER_THAN_START_TIME],
                    test: function (value) {
                        const startDateString: string | null = this.parent?.start
                        if (!startDateString || !value) {
                            return true
                        }
                        const startDate = new Date(parseJsDate(startDateString, false))
                        const endDate = new Date(parseJsDate(value, false))
                        return endDate >= startDate
                    },
                })
                .test({
                    message: ERROR_MESSAGES['time_window']['end'][ERROR_CODES.SCHEDULE_CHANGE_BACKWARD_NOT_ALLOWED],
                    test: function (value) {
                        if (!isCampaignLive) {
                            return true
                        } else {
                            const initialEndDateString: string | undefined = initialValues
                                ? _.get(initialValues, this.path, undefined)
                                : undefined
                            const initialEndDate: Date | undefined = initialEndDateString
                                ? new Date(initialEndDateString)
                                : undefined

                            const selectDate = new Date(parseJsDate(value, false))
                            return initialEndDate ? initialEndDate.getTime() <= selectDate.getTime() : true
                        }
                    },
                })
                .test({
                    message:
                        ERROR_MESSAGES['time_window']['end'][ERROR_CODES.CAMPAIGN_PAYOUT_DEADLINE_LESS_THAN_END_TIME],
                    test: function (value) {
                        if (!campaign.payout_deadline || isExternalUser) {
                            return true
                        }
                        const payoutDeadline = new Date(parseJsDate(campaign.payout_deadline, false))
                        const endDate = new Date(parseJsDate(value, false))
                        if (isCampaignDraftOrForEdit && !value && !isPublish?.current) return true
                        return payoutDeadline > endDate
                    },
                })
                .test({
                    message: ERROR_MESSAGES['time_window']['end'][ERROR_CODES.SCHEDULE_MUST_LATER_THAN_PARENT_STEP],
                    test: function (value) {
                        if (!previousStepTypeOrIndex || !value) {
                            return true
                        } else {
                            const currentStepEndDate = new Date(parseJsDate(value, false))
                            const rootSchemaOptions: any = this.options
                            const { from = null } = rootSchemaOptions
                            const rootValue = from && from[2] ? from[2].value : null
                            const previousStepTime =
                                rootValue && rootValue.type === 'custom'
                                    ? rootValue.steps[previousStepTypeOrIndex].time_window
                                    : rootValue
                                      ? rootValue[previousStepTypeOrIndex].time_window
                                      : null
                            const previousStepEndTime = previousStepTime ? previousStepTime?.end : null
                            const previousStepStartTime = previousStepTime ? previousStepTime?.start : null
                            const parentEndTime = previousStepEndTime
                                ? new Date(parseJsDate(previousStepEndTime, false))
                                : null
                            const parentStartTime = previousStepStartTime
                                ? new Date(parseJsDate(previousStepStartTime, false))
                                : null
                            const compareTime = parentEndTime ? parentEndTime : parentStartTime
                            if (compareTime) {
                                return currentStepEndDate >= compareTime
                            } else {
                                return true
                            }
                        }
                    },
                })
                .test({
                    message: ERROR_MESSAGES['time_window']['end'][ERROR_CODES.SCHEDULE_MUST_LATER_THAN_NOW],
                    test: function (value) {
                        if (!value) {
                            return true
                        }
                        const initialEndDateString = initialValues
                            ? _.get(initialValues, this.path, undefined)
                            : undefined
                        const initialEndDate = initialEndDateString ? new Date(initialEndDateString) : undefined
                        const endDate = new Date(value)
                        if (initialEndDate && initialEndDate.getTime() === endDate.getTime()) {
                            return true
                        } else {
                            return validateDateLaterThenNow(endDate, campaign.country)
                        }
                    },
                })
                .test({
                    message: compareStartAndEndDate.isRegistrationTimeComparison
                        ? ERROR_MESSAGES['time_window']['end'][
                              ERROR_CODES.REGISTRATION_SCHEDULE_MUST_END_EARLIER_THAN_TASK
                          ]
                        : ERROR_MESSAGES['time_window']['end'][ERROR_CODES.SCHEDULE_MUST_END_LATER_THAN_REGISTRATION],
                    test: function (value) {
                        if (!compareStartAndEndDate.end || !value) {
                            return true
                        } else {
                            const currentStepEndDate = new Date(parseJsDate(value, false))
                            const compareEndTime = new Date(compareStartAndEndDate.end)
                            if (compareEndTime) {
                                if (compareStartAndEndDate.isRegistrationTimeComparison) {
                                    return currentStepEndDate <= compareEndTime
                                } else {
                                    return currentStepEndDate >= compareEndTime
                                }
                            } else {
                                return true
                            }
                        }
                    },
                }),
        })
        .nullable()
}

export const SCHEMA_TIME_WINDOW_END_TIME_MUST_BE_N_HOURS_LATER_THAN_PREV_STEP = (
    previousStep: string | null,
    addHours: number,
) => {
    return yup.object().shape({
        start: yup.string().test({
            message: () =>
                i18n.t('errorMessage.businessLogic.proofEngagementStartDaysAfterPreviousStepStart', {
                    count: Math.ceil(addHours / 24),
                }),
            test: function (value) {
                const rootSchemaOptions: any = this.options
                const { from = null } = rootSchemaOptions
                const rootValue = from && from[2] ? from[2].value : null
                //predefined and custom has different rootValue schema , the predefined should use the previousStep to find the previous time_window
                //But the custom task is an array and the engagement is alway the last step
                const previousStepTime = previousStep
                    ? // to find predefined previous step time_window
                      rootValue[previousStep].time_window
                    : // to find custom task previous step time_window
                      rootValue.steps[rootValue.steps.length - 2].time_window
                if (!value || !previousStepTime?.start) {
                    return true
                }
                const previousStepEndDateWithHours = new Date(
                    parseJsDate(previousStepTime?.start, false, true, 0, Math.ceil(addHours / 24)),
                )
                const currentStepEndDate = new Date(parseJsDate(value, false))
                return currentStepEndDate >= previousStepEndDateWithHours
            },
        }),
        end: yup.string().test({
            message: () =>
                i18n.t('errorMessage.businessLogic.proofEngagementEndDaysAfterPreviousStepEnd', {
                    count: Math.ceil(addHours / 24),
                }),
            test: function (value) {
                const rootSchemaOptions: any = this.options
                const { from = null } = rootSchemaOptions
                const rootValue = from && from[2] ? from[2].value : null
                //predefined and custom has different rootValue schema , the predefined should use the previousStep to find the previous time_window
                //But the custom task is an array and the engagement is alway the last step
                const previousStepTime = previousStep
                    ? // to find predefined previous step time_window
                      rootValue[previousStep].time_window
                    : // to find custom task previous step time_window
                      rootValue.steps[rootValue.steps.length - 2].time_window
                if (!value || !previousStepTime?.end) {
                    return true
                }
                const previousStepEndDateWithHours = new Date(
                    parseJsDate(previousStepTime?.end, false, true, 0, Math.ceil(addHours / 24)),
                )
                const currentStepEndDate = new Date(parseJsDate(value, false))
                return currentStepEndDate >= previousStepEndDateWithHours
            },
        }),
    })
}

interface TestContextExtended {
    from: {
        value: any
    }[]
}

export const ENGAGEMENT_STEP_TIME_WINDOW_SCHEMA = (
    comparedStepType: string,
    idle_hours_to_submit_engagement?: number,
): yup.ObjectSchema<{
    start: string | null
    end: string | null
} | null> =>
    yup
        .object({
            step2End: yup.ref(`stepData.${comparedStepType}.time_window.end`),
            start: yup.string().nullable(),
            end: yup
                .mixed()
                .nullable()
                .test({
                    message: () =>
                        i18n.t('errorMessage.businessLogic.enterEndDateDaysAfterPreviousStep', {
                            count: idle_hours_to_submit_engagement
                                ? Math.ceil(idle_hours_to_submit_engagement / 24)
                                : 0,
                        }),
                    params: { idle_hours_to_submit_engagement },
                    test: function (this, value) {
                        const { from } = this as yup.TestContext & TestContextExtended
                        const comparedStepTypeEnd =
                            from && from.length > 1 && from[2].value
                                ? from[2].value[comparedStepType].time_window?.end
                                : undefined
                        if (comparedStepTypeEnd) {
                            const comparedStepTypeEndAddIdleHours = new Date(
                                parseJsDate(
                                    comparedStepTypeEnd,
                                    false,
                                    true,
                                    0,
                                    idle_hours_to_submit_engagement
                                        ? Math.ceil(idle_hours_to_submit_engagement / 24)
                                        : 0,
                                ),
                            )
                            const currentStepEnd = new Date(parseJsDate(value, false))
                            return comparedStepTypeEndAddIdleHours <= currentStepEnd
                        } else {
                            return true
                        }
                    },
                }),
        })
        .nullable()

export const PAYOUTS_SCHEMA = (isCampaignDraftOrForEdit: boolean) => {
    return {
        cost: yup
            .number()
            .nullable()
            .min(0, i18n.t('errorMessage.validation.enterNonNegativeNumbers'))
            .test({
                message: ERROR_MESSAGES['payouts']['cost'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: number | null) => {
                    if (isCampaignDraftOrForEdit || value === 0) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
        min: yup
            .number()
            .nullable()
            .integer(i18n.t('errorMessage.validation.enterMinimumNumberPayout'))
            .min(0, i18n.t('errorMessage.validation.enterNonNegativeNumbers'))
            .test({
                message: ERROR_MESSAGES['payouts']['min'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: number | null) => {
                    if (isCampaignDraftOrForEdit || value === 0) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
        max: yup
            .number()
            .nullable()
            .integer(i18n.t('errorMessage.validation.enterMaximumNumberPayout'))
            .min(0, i18n.t('errorMessage.validation.enterNonNegativeNumbers'))
            .test({
                message: ERROR_MESSAGES['payouts']['max'][ERROR_CODES.INPUT_REQUIRED],
                test: (value: number | null) => {
                    if (isCampaignDraftOrForEdit || value === 0) {
                        return true
                    } else {
                        return Boolean(value)
                    }
                },
            }),
    }
}

export const EXTRA_PAYOUT_SCHEMA = (isCampaignDraftOrForEdit: boolean) => {
    const payoutSchema = PAYOUTS_SCHEMA(isCampaignDraftOrForEdit)
    return yup.lazy((obj: object) =>
        yup.object(
            mapValues(obj, () =>
                yup.object().shape({
                    title: yup
                        .string()
                        .nullable()
                        .test({
                            message: ERROR_MESSAGES['payouts']['title'][ERROR_CODES.INPUT_REQUIRED],
                            test: (value: string | null) => {
                                if (isCampaignDraftOrForEdit) {
                                    return true
                                } else {
                                    return Boolean(value)
                                }
                            },
                        }),
                    description: yup
                        .string()
                        .nullable()
                        .test({
                            message: ERROR_MESSAGES['payouts']['description'][ERROR_CODES.INPUT_REQUIRED],
                            test: (value: string | null) => {
                                if (isCampaignDraftOrForEdit) {
                                    return true
                                } else {
                                    return Boolean(value)
                                }
                            },
                        }),
                    cost: payoutSchema.cost,
                    min: payoutSchema.min,
                    max: payoutSchema.max,
                }),
            ),
        ),
    )
}

export const CUSTOM_STEP_BLOCK_SCHEMA = (isCampaignDraftOrForEdit: boolean) =>
    yup.object().shape({
        type: yup.string(),
        title: yup
            .string()
            .when([], () =>
                isCampaignDraftOrForEdit
                    ? yup.string().nullable().notRequired()
                    : yup
                          .string()
                          .nullable()
                          .required(ERROR_MESSAGES['steps']['blocks']['title'][ERROR_CODES.INPUT_REQUIRED]),
            ),
        description: yup
            .string()
            .nullable()
            .when('type', {
                is: value => [BLOCK_TYPE.TEXT, BLOCK_TYPE.PHOTO_CAROUSEL].includes(value) && !isCampaignDraftOrForEdit,
                then: yup
                    .string()
                    .nullable()
                    .required(ERROR_MESSAGES['steps']['blocks']['description'][ERROR_CODES.INPUT_REQUIRED]),
            }),
        allow_download: yup.boolean().when('type', {
            is: BLOCK_TYPE.PHOTO_CAROUSEL,
            then: yup.boolean(),
        }),
        files: yup.array().when('type', {
            is: value => value === BLOCK_TYPE.PHOTO_CAROUSEL && !isCampaignDraftOrForEdit,
            then: yup.array().min(1, ERROR_MESSAGES['steps']['blocks']['files'][ERROR_CODES.INPUT_REQUIRED]),
        }),
        max_photos: yup
            .number()
            .nullable()
            .when('type', {
                is: type => type === BLOCK_TYPE.PHOTO_SUBMISSION && !isCampaignDraftOrForEdit,
                then: yup
                    .number()
                    .required(ERROR_MESSAGES['steps']['blocks']['max_photos'][ERROR_CODES.INPUT_REQUIRED])
                    .min(1, ERROR_MESSAGES['steps']['blocks']['max_photos'][ERROR_CODES.NUMBER_TOO_LOW])
                    .integer(i18n.t('errorMessage.validation.enterWholeNumber')),
            }),
    })
